import { StopNew } from "../../../ducks/data/route-with-stops/types";
import { useSelector } from "react-redux";
import {
  StopDriverArrived,
  StopDriverDeparted,
} from "../../SessionsPage/StopPage/StopDriverArrivedDeparted";
import { useParams } from "../../SessionsPage/hooks";
import { PickupDropoffButton } from "../../SessionsPage/StopPage/StopCompleted";
import AddImage from "../../SessionsPage/StopPage/AddImage";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import {
  selectDriverCanDeleteCargo,
  selectImagesAddedOnCargo,
  selectShouldShowArrivalDepartureButtonsNew,
} from "../../../ducks/data/route-with-stops/selectors";
import { useState } from "react";
import AddDropoffDialog from "../../SessionsPage/AddDropoffDialog";
import AddWeightModalAndButton from "../../SessionsPage/modals/AddWeightModalAndButton";
import AddCubicMetersModalAndButton from "../../SessionsPage/modals/AddCubicMetersModalAndButton";
import AddRefModalAndButton from "../../SessionsPage/modals/AddRefModalAndButton";
import AddCommentModalAndButton from "../../SessionsPage/modals/AddCommentModalAndButton";
import { useRoutePageContext } from "../RoutePageContext";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import {
  selectShouldShowAddProductButton,
  selectShouldShowCompleteStopButton,
} from "../../../ducks/auth/selectors";
import AddProductModalAndButton from "../../SessionsPage/modals/AddProductModalAndButton";
import AddScan from "../../SessionsPage/StopPage/AddScan";
import useFeature from "../../../hooks/useFeature";
import MoveToUndispatched from "./MoveToUndispatched";
import { isMobile } from "../../../helpers/mobile";
import ProductsButtons from "./ProductsButtons";
import UploadedDocumentsModalAndButton from "./UploadedDocumentsModalAndButton";
import DeleteStopModalAndButton from "./DeleteStopModalAndButton";

const StopButtons = ({
  stop,
  cargoDeliveryId,
  cargoInvoiceRef,
  hideAddDropoffButton,
  onAddDropoff,
}: {
  stop: StopNew;
  cargoDeliveryId: string;
  cargoInvoiceRef: string | null;
  hideAddDropoffButton?: boolean;
  onAddDropoff?: (stopId: string) => void;
}) => {
  const documentScanEnabled = useFeature("mobile-document-scan");
  const [showAddDropoff, setShowAddDropoff] = useState(false);

  const { t } = useTranslation(["stopPage"]);

  const filesPreviewEnabled = useFeature("generate-previews-for-files");

  const shouldShowAddProductButton = useSelector(
    selectShouldShowAddProductButton
  );

  const shouldShowArrivalDepartureButtons = useSelector(
    selectShouldShowArrivalDepartureButtonsNew(stop.id)
  );
  const shouldShowCompleteStopButton = useSelector(
    selectShouldShowCompleteStopButton
  );
  const driverCanDeleteCargo = useSelector(
    selectDriverCanDeleteCargo(stop.cargoId)
  );
  const imagesAddedOnStopCargo = useSelector(
    selectImagesAddedOnCargo(stop.cargoId)
  );

  const { reloadData } = useRoutePageContext();

  const stopWithCargoId = {
    ...stop,
    cargo_id: stop.cargoId,
  };

  const podNumberForStop = stop.attachments.filter(
    (a) => a.documentType === "POD"
  ).length;

  return (
    <div className="flex flex-col gap-2 mb-3">
      {showAddDropoff && (
        <AddDropoffDialog
          cargoId={stop.cargoId}
          onClose={() => setShowAddDropoff(false)}
          onSuccess={(stopId) => {
            setShowAddDropoff(false);
            reloadData();
            if (onAddDropoff) {
              onAddDropoff(stopId);
            }
          }}
        />
      )}
      {shouldShowArrivalDepartureButtons && (
        <div className="flex gap-2 items-center justify-between">
          <StopDriverArrived stop={stopWithCargoId} />
          <StopDriverDeparted stop={stopWithCargoId} />
        </div>
      )}
      {shouldShowCompleteStopButton && (
        <PickupDropoffButton stop={stopWithCargoId} />
      )}
      {shouldShowAddProductButton && (
        <>
          <ProductsButtons cargoDeliveryId={cargoDeliveryId} />
          <AddProductModalAndButton cargoDeliveryId={cargoDeliveryId} />
        </>
      )}
      {filesPreviewEnabled && (
        <UploadedDocumentsModalAndButton
          cargoId={stop.cargoId}
          uploadedDocumentsNumber={podNumberForStop}
        />
      )}
      {documentScanEnabled && isMobile && (
        <AddScan
          cargoId={stop.cargoId}
          stopId={stop.id}
          // scanCount={podNumberForStop}
          onSuccess={reloadData}
        />
      )}
      <AddImage
        cargoId={stop.cargoId}
        stopId={stop.id}
        added={imagesAddedOnStopCargo}
        imagesForStop={podNumberForStop}
        onSuccess={reloadData}
      />
      <AddCommentModalAndButton stop={stop} />
      <AddWeightModalAndButton stop={stop} />
      <AddCubicMetersModalAndButton stop={stop} />
      <AddRefModalAndButton stop={stop} cargoInvoiceRef={cargoInvoiceRef} />
      {!hideAddDropoffButton && (
        <>
          <div>{t("stopPage:addToOrder")}:</div>
          <Button
            onClick={() => setShowAddDropoff(true)}
            style={{ background: "var(--warning-500)" }}
            variant="contained"
            size="large"
          >
            <div className="flex gap-2 items-center">
              <AddLocationAltOutlinedIcon />
              <span>{t("stopPage:addDropoff")}</span>
            </div>
          </Button>
        </>
      )}
      <MoveToUndispatched cargoId={stop.cargoId} />
      {driverCanDeleteCargo && (
        <>
          <DeleteStopModalAndButton stop={stop} />
        </>
      )}
    </div>
  );
};

export default StopButtons;
